var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import { GetMeetingServiceRequestsForEventBySpaceIdDocument, } from '../../../generated/graphql';
import { SpaceInfoHeader } from './SpaceInfoHeader';
import { SpaceRequestFormDetails } from '../WorkplaceService/SpaceRequestFormDetails';
import { ServiceRequestDetails } from '../WorkplaceService/ServiceRequestDetails';
import { useWatch } from 'react-hook-form';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { useCallback, useMemo } from 'react';
import { SpaceInfoWrapper } from './EventSpaceWrapper';
import { useSpaceDetailsContext } from './SpaceDetailsContext';
import { LocationLevel } from './components/LocationLevel';
import { useCalendarIntegrationsContext } from '../../context/CalendarIntegrationContext';
import { useEventComposerGlobalContext } from '../../composers/EventComposerGlobalContext';
import { getEventOrganizer } from '../../utils/spaceOrganizerDisplay';
/* @TODO: This component is a bit messy. We should refactor it to be more modular. */
export var SpaceDetails = function (_a) {
    var spaceId = _a.spaceId;
    var t = useTranslation('meetingRooms').t;
    /* @TODO Remodify this component to accept an array of spaces */
    var formSpaces = useWatch({
        name: 'spaces',
    });
    var eventUid = useWatch({ name: 'uid' });
    var _b = useSpaceDetailsContext(), setWorkplaceServiceRequestFormStatus = _b.setWorkplaceServiceRequestFormStatus, spaceForMeetingServiceData = _b.spaceForMeetingServiceData, refetchSpaceForMeetingService = _b.refetchSpaceForMeetingService, requestOnlySpace = _b.requestOnlySpace, handleGetSpaceForMeetingService = _b.handleGetSpaceForMeetingService, initialEvent = _b.initialEvent;
    var isSpaceRequestOnlyEvent = !!requestOnlySpace;
    var checkFlag = useFeatureFlags().checkFlag;
    var composerMode = useEventComposerGlobalContext().composerMode;
    var _c = useCalendarIntegrationsContext(), selectedCalendar = _c.selectedCalendar, isLoadingCalendarIntegration = _c.isLoadingCalendarIntegration;
    var allowSpaceRequestFormFeatureFlag = checkFlag('hydra(space-request-forms)', false);
    var space = formSpaces === null || formSpaces === void 0 ? void 0 : formSpaces.find(function (space) { return space.id === spaceId; });
    /* @TODO get the service details for the space Id */
    var serviceDetails = spaceForMeetingServiceData === null || spaceForMeetingServiceData === void 0 ? void 0 : spaceForMeetingServiceData.getSpacesByIds[0];
    var apolloClient = useApolloClient();
    var cacheVariables = useMemo(function () {
        var _a;
        return ({
            spaceId: spaceId.toString(),
            eventInstance: {
                robinEventId: undefined,
                ical: {
                    // Keep behavior aligned with `useWorkplaceServiceRequest`, which may
                    // pass `undefined` uid during composer initialization.
                    uid: eventUid,
                    recurrenceId: (_a = initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.recurrenceId) !== null && _a !== void 0 ? _a : null,
                },
            },
        });
    }, [eventUid, spaceId, initialEvent === null || initialEvent === void 0 ? void 0 : initialEvent.recurrenceId]);
    var onRemoveServiceRequest = useCallback(function (serviceRequestId) {
        // Optimistically update the Apollo cache so the removed request
        // disappears immediately.
        try {
            var existing = apolloClient.readQuery({
                query: GetMeetingServiceRequestsForEventBySpaceIdDocument,
                variables: cacheVariables,
            });
            if (existing) {
                var nextGetSpacesByIds = existing.getSpacesByIds.map(function (space) {
                    var _a, _b;
                    if (space.id !== spaceId.toString())
                        return space;
                    return __assign(__assign({}, space), { meetingServiceRequests: (_b = (_a = space.meetingServiceRequests) === null || _a === void 0 ? void 0 : _a.filter(function (request) { return request.id !== serviceRequestId; })) !== null && _b !== void 0 ? _b : [] });
                });
                apolloClient.writeQuery({
                    query: GetMeetingServiceRequestsForEventBySpaceIdDocument,
                    variables: cacheVariables,
                    data: {
                        __typename: 'Query',
                        getSpacesByIds: nextGetSpacesByIds,
                    },
                });
            }
        }
        catch (_a) {
            /* noop */
        }
        // Ensure server state is reflected (network-only) to handle cases where
        // the Apollo cache doesn't have this query entry yet.
        handleGetSpaceForMeetingService(cacheVariables, 'cache-first');
    }, [apolloClient, cacheVariables, handleGetSpaceForMeetingService, spaceId]);
    var hasAmenities = useMemo(function () {
        return ((space === null || space === void 0 ? void 0 : space.amenities) && space.amenities.length > 0) || (space === null || space === void 0 ? void 0 : space.capacity);
    }, [space]);
    var showSpaceAsOrganizer = getEventOrganizer({
        spaceIsOrganizer: space === null || space === void 0 ? void 0 : space.isOrganizer,
        composerMode: composerMode,
        isLoadingCalendarIntegration: isLoadingCalendarIntegration,
        hasSelectedPersonalCalendar: !!selectedCalendar,
    }) === 'space';
    return (_jsxs(_Fragment, { children: [_jsx(SpaceInfoWrapper, { showBorder: false, children: _jsx(SpaceInfoHeader, { name: space === null || space === void 0 ? void 0 : space.name, showOrganizerLabel: showSpaceAsOrganizer, locationText: (space === null || space === void 0 ? void 0 : space.locationId) &&
                        (space === null || space === void 0 ? void 0 : space.levelId) && (_jsx(LocationLevel, { locationId: space.locationId.toString(), levelId: space.levelId.toString() })), capacity: hasAmenities ? (space === null || space === void 0 ? void 0 : space.capacity) || undefined : undefined, amenities: hasAmenities ? (space === null || space === void 0 ? void 0 : space.amenities) || [] : [], image: space === null || space === void 0 ? void 0 : space.image, imageAlt: t('create_event.space_image_alt', {
                        spaceName: space === null || space === void 0 ? void 0 : space.name,
                    }) }) }), isSpaceRequestOnlyEvent && allowSpaceRequestFormFeatureFlag && (_jsx(SpaceRequestFormDetails, { setWorkplaceServiceRequestFormStatus: setWorkplaceServiceRequestFormStatus, spaceForMeetingServiceData: spaceForMeetingServiceData, refetchSpaceForMeetingService: refetchSpaceForMeetingService })), serviceDetails && (_jsx(ServiceRequestDetails, { serviceDetails: serviceDetails, onEditServiceRequest: function (serviceRequestId) {
                    return setWorkplaceServiceRequestFormStatus({
                        type: 'edit',
                        serviceRequestId: serviceRequestId,
                    });
                }, onRemoveServiceRequest: onRemoveServiceRequest }))] }));
};
